import { Label } from "@gigpro/ui/Label";
import { LinearProgress } from "@gigpro/ui/LinearProgress";
import { Switch } from "@gigpro/ui/Switch";
import { cn } from "@gigpro/ui/tailwind/cn";
import { toast } from "@gigpro/ui/Toast";
import type { Certificate } from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import { useAdminPro, useUpdateCertification } from "api/queries/admin";
import * as R from "remeda";
import { certificatesQuery, skillsQuery } from "./queries";

type CertificationsContainerProps = { className?: string; proId: number };

export function AdminCertificationsContainer({ className, proId }: Readonly<CertificationsContainerProps>) {
  const skills = useQuery(skillsQuery());
  const skillIdLookup = skills.data ? R.mapToObj(skills.data, (skill) => [skill.id, skill]) : undefined;

  const proProfile = useAdminPro({ id: proId });
  const certificates = useQuery(certificatesQuery());

  const proActiveCerts = new Set(
    proProfile.data?.pro_certifications
      ?.filter((cert) => cert.status === "CERTIFIED")
      .map((cert) => cert.certificate.id),
  );

  const updateCertification = useUpdateCertification({
    onMutate: ({ certificate_id }) => toast.loading("Updating certification", { id: certificate_id }),
    onSuccess: (_data, { certificate_id }) => toast.success("Certification updated", { id: certificate_id }),
    onError: (error, { certificate_id }) => toast.error(error.message, { id: certificate_id }),
  });

  const isUpdating = certificates.isFetching || updateCertification.isPending || proProfile.isFetching;
  const isLoading = certificates.isLoading || proProfile.isLoading || skills.isLoading;

  const grouped = R.pipe(
    certificates.data?.results ?? [],
    R.groupBy((cert) => {
      if (cert.skill_id) {
        const groupName = skillIdLookup?.[cert.skill_id]?.group_name;
        if (!groupName) return "Other";
        return groupName;
      }
      if (cert.brand_id) return "Brand";
      return "Other";
    }),
    R.entries(),
    // sort groups by name, with "Brand" first and "Other" last
    // oxlint-disable-next-line unicorn/no-array-sort -- remeda R.sort returns a new array
    R.sort(([a], [b]) => {
      if (a === "Brand" || b === "Other") return -1;
      if (b === "Brand" || a === "Other") return 1;
      return a.localeCompare(b);
    }),
    // sort skills within each group by name
    R.map(
      ([group, certs]) =>
        [group, certs.toSorted((a, b) => a.name.localeCompare(b.name))] as [string, Array<Certificate>],
    ),
  );

  return (
    <div
      className={cn("grid w-full grid-cols-[repeat(auto-fit,minmax(210px,1fr))] grid-rows-[masonry] gap-4", className)}
    >
      <LinearProgress className={cn("col-span-full -my-2 transition-opacity", !isUpdating && "opacity-0")} />
      {!isLoading &&
        grouped.map(([group, certs]) => (
          <div key={group} className="flex w-full flex-col gap-1">
            <h3 className="text-large font-semibold">{group}</h3>
            {certs.map((cert) => (
              <Label key={cert.id} htmlFor={cert.id} className="flex items-center gap-1 px-2 py-1 text-normal">
                <Switch
                  id={cert.id}
                  disabled={isLoading && updateCertification.variables?.certificate_id === cert.id}
                  checked={proActiveCerts.has(cert.id)}
                  onCheckedChange={(checked) => {
                    if (!proProfile.data) return;
                    const existingCert = proProfile.data.pro_certifications?.find((c) => c.certificate.id === cert.id);
                    updateCertification.mutate({
                      id: existingCert?.id,
                      certificate_id: cert.id,
                      status: checked ? "CERTIFIED" : "DISQUALIFIED",
                      pro_id: proProfile.data.user_id,
                    });
                  }}
                />
                {cert.name}
              </Label>
            ))}
          </div>
        ))}
    </div>
  );
}
